import { useState, type FormEvent, type KeyboardEvent } from "react";
import { isAxiosError } from "axios";
import { Button, Field, Icon, Input } from "../ui";
import type { Project } from "../types";
import { useCreateProject } from "../hooks/useCreateProject";

// Server-side mirror of D-18 (PROJ-03): list 0..10 items, each
// 1..200 chars after trimming, non-blank. Frontend validation here
// matches the Pydantic v2 ProjectCreate validator so the user sees
// inline feedback instead of a generic 422.
const MAX_DESCRIPTORS = 10;
const MAX_DESCRIPTOR_LEN = 200;

export interface NewProjectModalProps {
  onClose: () => void;
  onCreated: (project: Project) => void;
}

export function NewProjectModal({ onClose, onCreated }: NewProjectModalProps) {
  const [name, setName] = useState("");
  const [iupac, setIupac] = useState("");
  const [cas, setCas] = useState("");
  const [formula, setFormula] = useState("");
  const [mw, setMw] = useState("");
  const [descriptors, setDescriptors] = useState<string[]>([]);
  const [descriptorDraft, setDescriptorDraft] = useState("");
  const [descriptorError, setDescriptorError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateProject();

  const tryAddDescriptor = () => {
    const value = descriptorDraft.trim();
    if (!value) {
      setDescriptorError(null);
      setDescriptorDraft("");
      return;
    }
    if (descriptors.length >= MAX_DESCRIPTORS) {
      setDescriptorError(`Máximo de ${MAX_DESCRIPTORS} descritores.`);
      return;
    }
    if (value.length > MAX_DESCRIPTOR_LEN) {
      setDescriptorError(`Cada descritor deve ter até ${MAX_DESCRIPTOR_LEN} caracteres.`);
      return;
    }
    setDescriptors([...descriptors, value]);
    setDescriptorDraft("");
    setDescriptorError(null);
  };

  const onDescriptorKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      tryAddDescriptor();
    } else if (e.key === "Backspace" && !descriptorDraft && descriptors.length) {
      e.preventDefault();
      setDescriptors(descriptors.slice(0, -1));
    }
  };

  const removeDescriptor = (idx: number) => {
    setDescriptors(descriptors.filter((_, i) => i !== idx));
    setDescriptorError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    try {
      // Phase 1 backend (PROJ endpoints in plan 01-04) accepts only drug_api_name + extra_descriptors;
      // iupac/cas/formula/mw inputs stay in the UI but are populated by Phase 2 PubChem fetch (PLANO §3.3).
      const created = await mutation.mutateAsync({
        drug_api_name: name.trim(),
        ...(descriptors.length > 0 ? { extra_descriptors: descriptors } : {}),
      });
      onCreated(created);
    } catch (err) {
      const detail =
        isAxiosError(err) &&
        typeof err.response?.data === "object" &&
        err.response?.data !== null &&
        "detail" in err.response.data
          ? String((err.response.data as { detail: unknown }).detail)
          : "Não foi possível criar a monografia. Tente novamente.";
      setError(detail);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 anim-fade"
      style={{ background: "rgba(10,10,10,0.4)" }}
      onClick={onClose}
    >
      <div className="absolute inset-0 flex items-start justify-center pt-16 px-6">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-[var(--paper)] border border-[var(--ink)] w-full max-w-2xl page-edge anim-rise"
        >
          <div className="flex items-center justify-between px-8 py-5 border-b-2 border-[var(--ink)]">
            <div>
              <div className="eyebrow">§ Nova entrada</div>
              <div className="display-4 mt-1">Cadastrar IFA</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-[var(--ink-3)] hover:text-[var(--ink)] p-1"
            >
              <Icon.X size={20} />
            </button>
          </div>

          <form onSubmit={submit} className="px-8 py-7 space-y-6">
            <Field
              label="Nome do IFA"
              required
              hint="Português · denominação ANVISA quando aplicável"
            >
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ex.: Sertralina Hidrocloreto"
              />
            </Field>

            <Field label="Nome IUPAC" hint="Opcional · pode preencher depois">
              <Input
                mono
                value={iupac}
                onChange={(e) => setIupac(e.target.value)}
                placeholder="(1S,4S)-4-(3,4-dichlorophenyl)…"
              />
            </Field>

            <Field
              label="Descritores adicionais"
              hint={`Pressione Enter ou , para adicionar · até ${MAX_DESCRIPTORS} chips · até ${MAX_DESCRIPTOR_LEN} caracteres cada`}
            >
              <div
                className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-[var(--ink)] bg-[var(--paper)] min-h-[2.5rem]"
                onClick={(e) => {
                  const target = e.currentTarget.querySelector("input");
                  target?.focus();
                }}
              >
                {descriptors.map((d, i) => (
                  <span
                    key={`${d}-${i}`}
                    className="inline-flex items-center gap-1 px-2 h-6 border border-[var(--ink)] bg-[var(--paper)] text-[12px] mono"
                  >
                    {d}
                    <button
                      type="button"
                      onClick={() => removeDescriptor(i)}
                      aria-label={`Remover descritor ${d}`}
                      className="text-[var(--ink-3)] hover:text-[var(--strike)] -mr-0.5"
                    >
                      <Icon.X size={12} strokeWidth={2} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={descriptorDraft}
                  onChange={(e) => setDescriptorDraft(e.target.value)}
                  onKeyDown={onDescriptorKeyDown}
                  onBlur={tryAddDescriptor}
                  placeholder={
                    descriptors.length === 0
                      ? "ex.: metabólito, cloridrato, sertralina HCl"
                      : ""
                  }
                  className="flex-1 min-w-[12ch] outline-none bg-transparent text-[14px] py-0.5"
                />
              </div>
              {descriptorError && (
                <div className="caption mt-1 text-[var(--strike)]" role="alert">
                  {descriptorError}
                </div>
              )}
            </Field>

            <div className="grid grid-cols-3 gap-5">
              <Field label="CAS">
                <Input
                  mono
                  value={cas}
                  onChange={(e) => setCas(e.target.value)}
                  placeholder="79559-97-0"
                />
              </Field>
              <Field label="Fórmula molecular">
                <Input
                  mono
                  value={formula}
                  onChange={(e) => setFormula(e.target.value)}
                  placeholder="C17H17Cl2N·HCl"
                />
              </Field>
              <Field label="Peso molecular">
                <Input
                  mono
                  value={mw}
                  onChange={(e) => setMw(e.target.value)}
                  placeholder="342,69 g/mol"
                />
              </Field>
            </div>

            {error && (
              <div
                role="alert"
                className="caption px-3 py-2 border border-[var(--strike)] text-[var(--strike)]"
              >
                {error}
              </div>
            )}

            <div className="border-t border-[var(--rule)] pt-5 flex items-center justify-between gap-4">
              <div className="caption max-w-md">
                Esta monografia entrará na Etapa 1 com a busca regulatória pronta para iniciar.
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="line"
                  onClick={onClose}
                  disabled={mutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="ink"
                  iconRight={Icon.ArrowRight}
                  disabled={!name.trim() || mutation.isPending}
                >
                  {mutation.isPending ? "Criando…" : "Criar monografia"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
